import { Component, Input, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  TeamSettings,
  TeamContent,
  TeamLayoutVariant,
  TeamMember,
  TeamMemberSettings,
  SECTION_THEME,
} from './section-renderer.models';

/**
 * Team Section Renderer
 * Full-width team sections with multiple layout variants.
 * Based on Tailwind Plus team examples.
 */
@Component({
  selector: 'dcp-team-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="py-24 sm:py-32" [style.background-color]="backgroundColor()">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <!-- Section Header -->
        @if (content().title || content().subtitle) {
          <div class="mx-auto max-w-2xl lg:mx-0">
            @if (content().title) {
              <h2 class="text-pretty text-4xl font-semibold tracking-tight sm:text-5xl"
                  [style.color]="theme.textColor">
                {{ content().title }}
              </h2>
            }
            @if (content().subtitle) {
              <p class="mt-6 text-lg/8" [style.color]="theme.textMutedColor">
                {{ content().subtitle }}
              </p>
            }
          </div>
        }
        
        @switch (layoutVariant()) {
          @case ('large-images') {
            <!-- Large Images with Bio -->
            <ul role="list" class="mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-2 xl:grid-cols-3">
              @for (member of resolvedMembers(); track member.id) {
                <li>
                  @if (member.imageUrl) {
                    <img 
                      [src]="member.imageUrl" 
                      [alt]="member.name" 
                      class="aspect-[3/2] w-full rounded-2xl object-cover" 
                    />
                  } @else {
                    <div class="aspect-[3/2] w-full rounded-2xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                  }
                  <h3 class="mt-6 text-lg/8 font-semibold tracking-tight" [style.color]="theme.textColor">
                    {{ member.name }}
                  </h3>
                  @if (settings().showRole !== false) {
                    <p class="text-base/7" [style.color]="theme.textMutedColor">{{ member.role }}</p>
                  }
                  @if (settings().showEmail && member.email) {
                    <a [href]="'mailto:' + member.email" 
                       class="inline-flex items-center gap-1 mt-2 text-sm hover:underline"
                       [style.color]="theme.primaryColor">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      {{ member.email }}
                    </a>
                  }
                  @if (settings().showBio && member.bio) {
                    <div class="mt-4 text-base/7 prose prose-sm max-w-none" 
                         [style.color]="theme.textMutedColor"
                         [innerHTML]="sanitizeBio(member.bio)"></div>
                  }
                </li>
              }
            </ul>
          }
          
          @case ('small-avatars') {
            <!-- Small Circular Avatars -->
            <ul role="list" class="mx-auto mt-20 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-14 sm:grid-cols-3 lg:mx-0 lg:max-w-none lg:grid-cols-4 xl:grid-cols-6">
              @for (member of resolvedMembers(); track member.id) {
                <li class="text-center">
                  @if (member.imageUrl) {
                    <img 
                      [src]="member.imageUrl" 
                      [alt]="member.name" 
                      class="mx-auto size-24 rounded-full object-cover" 
                    />
                  } @else {
                    <div class="mx-auto size-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                  }
                  <h3 class="mt-4 text-base/7 font-semibold tracking-tight" [style.color]="theme.textColor">
                    {{ member.name }}
                  </h3>
                  @if (settings().showRole !== false) {
                    <p class="text-sm/6" [style.color]="theme.textMutedColor">{{ member.role }}</p>
                  }
                  @if (settings().showEmail && member.email) {
                    <a [href]="'mailto:' + member.email" 
                       class="inline-flex items-center justify-center gap-1 mt-1 text-xs hover:underline"
                       [style.color]="theme.primaryColor">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      {{ member.email }}
                    </a>
                  }
                </li>
              }
            </ul>
          }
          
          @default {
            <!-- Medium Cards (default grid) -->
            <ul role="list" 
                class="mx-auto mt-20 grid max-w-2xl gap-x-8 gap-y-14 lg:mx-0 lg:max-w-none"
                [class.grid-cols-1]="true"
                [class.sm:grid-cols-2]="true"
                [class.lg:grid-cols-3]="(settings().columns || 4) >= 3"
                [class.xl:grid-cols-4]="(settings().columns || 4) >= 4">
              @for (member of resolvedMembers(); track member.id) {
                <li>
                  @if (member.imageUrl) {
                    <img 
                      [src]="member.imageUrl" 
                      [alt]="member.name" 
                      class="aspect-[14/13] w-full rounded-2xl object-cover outline outline-1 -outline-offset-1 outline-black/5 dark:outline-white/10" 
                    />
                  } @else {
                    <div class="aspect-[14/13] w-full rounded-2xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center outline outline-1 -outline-offset-1 outline-black/5 dark:outline-white/10">
                      <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                  }
                  <h3 class="mt-6 text-lg/8 font-semibold tracking-tight" [style.color]="theme.textColor">
                    {{ member.name }}
                  </h3>
                  @if (settings().showRole !== false) {
                    <p class="text-base/7" [style.color]="theme.textMutedColor">{{ member.role }}</p>
                  }
                  @if (settings().showEmail && member.email) {
                    <a [href]="'mailto:' + member.email" 
                       class="inline-flex items-center gap-1 mt-1 text-sm hover:underline"
                       [style.color]="theme.primaryColor">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      {{ member.email }}
                    </a>
                  }
                  @if (settings().showBio && member.bio) {
                    <div class="mt-4 text-base/7 prose prose-sm max-w-none" 
                         [style.color]="theme.textMutedColor"
                         [innerHTML]="sanitizeBio(member.bio)"></div>
                  }
                  @if (settings().showLocation && member.location) {
                    <p class="text-sm/6" [style.color]="theme.textMutedColor">{{ member.location }}</p>
                  }
                </li>
              }
            </ul>
          }
        }
        
        <!-- Empty state -->
        @if (!resolvedMembers().length) {
          <div class="mt-20 text-center py-12 border-2 border-dashed rounded-lg" [style.border-color]="theme.borderColor">
            <svg class="mx-auto h-12 w-12" [style.color]="theme.textMutedColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            <h3 class="mt-2 text-sm font-semibold" [style.color]="theme.textColor">No team members</h3>
            <p class="mt-1 text-sm" [style.color]="theme.textMutedColor">Add team members to display them here.</p>
          </div>
        }
      </div>
    </div>
  `,
})
export class TeamRendererComponent {
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  private readonly sanitizer = inject(DomSanitizer);
  protected get theme() { return this.themeSignal(); }

  /** Section settings */
  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as TeamSettings);
  }
  private _settings = signal<TeamSettings>({});
  readonly settings = this._settings.asReadonly();

  /** Section content */
  @Input() set contentInput(value: Record<string, unknown>) {
    const content = value as unknown as TeamContent;
    this._content.set({
      ...content,
      memberBios: content.memberBios || {},
    });
  }
  private _content = signal<TeamContent>({});
  readonly content = this._content.asReadonly();

  /** Resolved team members: combines settings members with content bios */
  readonly resolvedMembers = computed<TeamMember[]>(() => {
    const settings = this._settings();
    const content = this._content();
    const members = settings.members || [];
    const bios = content.memberBios || {};
    
    return members.map(member => ({
      id: member.id,
      name: member.name,
      role: member.role,
      email: member.email,
      // imageUrl is resolved by page-editor and added to member settings
      imageUrl: (member as TeamMemberSettings & { imageUrl?: string }).imageUrl,
      bio: bios[member.id] || '',
    }));
  });

  /** Computed layout variant with fallback */
  readonly layoutVariant = computed<TeamLayoutVariant>(() => {
    return (this._settings().layoutVariant as TeamLayoutVariant) || 'medium-cards';
  });

  /** Computed background color */
  readonly backgroundColor = computed(() => {
    const settings = this._settings();
    if (settings.backgroundType === 'solid' && settings.backgroundColor) {
      return settings.backgroundColor;
    }
    return this.themeSignal()?.backgroundColor || '#ffffff';
  });

  /** Sanitize HTML bio for safe rendering */
  sanitizeBio(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
